import Http from '../../../../network/Http'
import {DisasterModeInfo} from './NatGetDisasterModeInfo'
import {ApiResponse} from '../../../../network/response/ApiResponse'
import {ContentType} from '../../../../network/Constants'
import {NatGetDisasterTopicCategory} from './NatGetDisasterTopicCategory'
import {NatSearchDisasterInfoListResponse} from './NatSearchDisasterInfoListResponse'
import {NatGetDisasterPreventionsInfoResponse} from './NatGetDisasterPreventionsInfo'
import {NatDeleteDisasterInfo} from './NatDeleteDisasterInfo'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatBaseResponseInterface} from '../../common/repository/NatBaseResponseInterface'
import {NatPostAdminDisasterRegister, NatPostAdminDisasterRegisterParams} from './NatPostAdminDisasterRegister'
import {
    NatPutDisasterPreventionRequest,
    NatPutDisasterPreventionResponse
} from './NatPutDisasterPrevention'
import {DisasterModePeriodResponse} from './NatDisasterModePeriodResponse'
import {
    NatPostDisasterTopicRegisterResult,
    NatPostDisasterTopicRegisterRequest
} from './NatPostDisasterTopicRegister'
import {NatGetDisasterTopic} from './NatGetDisasterTopic'
import {NatGetDisasterComment} from './NatGetDisasterComment'
import {NatPostDisasterCommentRequest, NatPostDisasterCommentResponse} from './NatPostDisasterComment'
import {
    NatPostDisasterCommentFileUploadRequest,
    NatPostDisasterCommentFileUploadResponse
} from './NatPostDisasterCommentFileUpload'
import {NatPutDisasterCommentRequest, NatPutDisasterCommentResponse} from './NatPutDisasterComment'
import {NatPostDisasterCommentReadRequest, NatPostDisasterCommentReadResponse} from './NatPostDisasterCommentRead'
import {NatGetDisasterCommentList, SearchCondition} from './NatGetDisasterCommentList'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {NatGetDisasterTopicList} from './NatGetDisasterTopicList'
import {NatPutAdminDisasterEditParams, NatPutAdminDisasterTopicEdit} from './NatPutAdminDisasterTopicEdit'
import {NatSearchDisasterModeStatusList, SearchCondition as DisasterModeSearchCondition} from './NatSearchDisasterModeStatusList'
import {NatGetDisasterPreventionsInfoForResidentResponse} from './NatGetDisasterPreventionInfoForResident'
import {NatPutDisasterPreventionEdit, NatPutDisasterPreventionEditParams} from './NatPutDisasterPreventionEdit'
import {
    NatPostDisasterPreventionFileRequest,
    NatPostDisasterPreventionFileResponse
} from './NatPostDisasterPreventionFile'
import {NatPutDisasterTopicEditRequest} from './NatPutDisasterTopicEdit';
import {NatGetDisasterPreventionsInfoListResponse} from './NatGetDisasterPreventionInfoList'
import {NatGetDisasterTopicForAdmin} from './NatGetDisasterTopicForAdmin'
import {NatGetDisasterCommentForAdmin} from './NatGetDisasterCommentForAdmin'
import {
    NatPostDisasterCommentForAdminRequest,
    NatPostDisasterCommentForAdminResponse
} from './NatPostDisasterCommentForAdmin'
import {
    NatPutDisasterCommentForAdminRequest,
    NatPutDisasterCommentForAdminResponse
} from './NatPutDisasterCommentForAdmin'

export interface DisasterVoidResponse extends NatBaseResponseInterface {
    details: null
}

export interface DisasterVoidResponse extends NatBaseResponseInterface {
    details: null
}

export default class NatDisasterRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    // モード設定状況取得
    async getDisasterMode(
        condominiumCd: string
    ): Promise<DisasterModeInfo> {
        const response: ApiResponse<DisasterModeInfo> = await this.http.get(`/api/common/condominiums/${condominiumCd}/disasters/mode`)
        return response.details
    }

    // モード解除（居住者）
    async releaseDisasterModeResident(params: {
        condominiumCd: string,
        version: Nullable<number>
    }): Promise<DisasterVoidResponse> {
        return await this.http.put(`/api/resident/condominiums/${params.condominiumCd}/disasters/mode/release`,
            {version: params.version}
        )
    }

    // モード延長（居住者）
    async extensionDisasterModeResident(params: {
        condominiumCd: string,
        version: Nullable<number>
    }): Promise<DisasterModePeriodResponse> {
        return await this.http.put(`/api/resident/condominiums/${params.condominiumCd}/disasters/mode/extension`,
            {version: params.version}
        )
    }

    // トピック一覧取得（居住者）
    async getDisasterTopic(condominiumCd: string, pastFlag?: boolean):
        Promise<NatGetDisasterTopicCategory> {
        let getDisasterTopicUrl = `/api/resident/condominiums/${condominiumCd}/disasters/topics`

        if (pastFlag) {
            getDisasterTopicUrl += `?pastFlag=1`
        }
        return await this.http.get(getDisasterTopicUrl)
    }

    // トピック取得（居住者）
    async getDisasterTopicDetail(condominiumCd: string, topicId: number):
        Promise<NatGetDisasterTopic> {
        return await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}`
        )
    }

    // トピック削除（居住者）
    async deleteDisasterTopic(condominiumCd: string, topicId: number):
        Promise<NatBaseResponseInterface> {
        return await this.http.delete(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}`
        )
    }

    // コメント取得（居住者）
    async getDisasterComment(condominiumCd: string, topicId: number):
        Promise<NatGetDisasterComment> {
        return await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments`
        )
    }

    // コメント投稿
    async createDisasterComment(
        condominiumCd: string,
        topicId: number,
        request: NatPostDisasterCommentRequest
    ): Promise<NatPostDisasterCommentResponse> {
        return await this.http.post(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments`,
            request,
            ContentType.JSON
        )
    }

    // コメント投稿（ファイルアップロード）
    async fileUploadDisasterComment(
        condominiumCd: string,
        topicId: number,
        commentId: number,
        request: NatPostDisasterCommentFileUploadRequest
    ): Promise<NatPostDisasterCommentFileUploadResponse> {
        return await this.http.post(
            `/api/common/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}/files`,
            request,
            ContentType.JSON
        )
    }

    // コメント更新
    async updateDisasterComment(
        condominiumCd: string,
        topicId: number,
        commentId: number,
        request: NatPutDisasterCommentRequest
    ): Promise<NatPutDisasterCommentResponse> {
        return await this.http.put(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`,
            request
        )
    }

    // コメント削除（居住者）
    async deleteDisasterComment(condominiumCd: string, topicId: number, commentId: number):
        Promise<NatBaseResponseInterface> {
        return await this.http.delete(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`
        )
    }

    // コメント既読
    async createDisasterCommentRead(
        condominiumCd: string,
        topicId: number,
        request: NatPostDisasterCommentReadRequest
    ): Promise<NatPostDisasterCommentReadResponse> {
        return await this.http.post(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/read`,
            request,
            ContentType.JSON
        )
    }

    // 防災情報検索（管理）
    async searchDisasterInfoList(
        condominiumCd: string
    ): Promise<NatSearchDisasterInfoListResponse> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/preventions/search`, {}, ContentType.JSON,
        )
    }

    // 防災情報取得
    async getDisasterInfo(
        condominiumCd: string,
        disasterPreventionId: number
    ): Promise<NatGetDisasterPreventionsInfoResponse> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`
        )
    }

    // 防災情報削除
    async deleteDisasterInfo(
        condominiumCd: string,
        disasterPreventionId: number
    ): Promise<NatDeleteDisasterInfo> {
        return await this.http.delete(`/api/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`)
    }

    // モード設定（居住者）
    async setDisasterModeResident(params: {
        condominiumCd: string,
        trainingMode: string,
        version: Nullable<number>
    }): Promise<DisasterVoidResponse> {
        return await this.http.put(`/api/resident/condominiums/${params.condominiumCd}/disasters/mode/setting`,
            {trainingMode: params.trainingMode, version: params.version}
        )
    }

    // モード設定状況検索
    async searchDisasterModeStatusList(
        searchCondition: DisasterModeSearchCondition,
        searchConditionSortInfo: SortOrder[],
    ): Promise<NatSearchDisasterModeStatusList> {
        const sortInfos: string[] = []
        if (searchConditionSortInfo) {
            searchConditionSortInfo.forEach((sortInfo) => {
                sortInfos.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }

        return await this.http.post(
            `/api/admin/disasters/mode/statuses/search`,
            {
                condominiumGroupId: searchCondition.condominiumGroupId || null,
                condominiumCdList: searchCondition.condominiumCdList || null,
                condominiumCd: searchCondition.condominiumCd || null,
                condominiumName: searchCondition.condominiumName || null,
                prefectureCd: searchCondition.prefectureCd || null,
                address2: searchCondition.address2 || null,
                disasterMode: searchCondition.disasterMode || null,
                disasterPrevention: searchCondition.disasterPrevention || null,
                pageNum: searchCondition.pageNum ?? null,
                page: searchCondition.page ?? null,
                sortInfo: sortInfos,
            },
            ContentType.JSON
        )
    }

    static exportDisasterModeStatusListByCsvData(searchCondition: DisasterModeSearchCondition): string {
        const searchParams: string[] = []
        if (searchCondition.condominiumGroupId) {
            searchParams.push(`condominiumGroupId=${searchCondition.condominiumGroupId}`)
        }
        if (searchCondition.condominiumCdList) {
            searchParams.push(`condominiumCdList=${searchCondition.condominiumCdList}`)
        }
        if (searchCondition.condominiumCd) {
            searchParams.push(`condominiumCd=${searchCondition.condominiumCd}`)
        }
        if (searchCondition.condominiumName) {
            searchParams.push(`condominiumName=${searchCondition.condominiumName}`)
        }
        if (searchCondition.prefectureCd) {
            searchParams.push(`prefectureCd=${searchCondition.prefectureCd}`)
        }
        if (searchCondition.address2) {
            searchParams.push(`address2=${searchCondition.address2}`)
        }
        if (searchCondition.disasterMode) {
            searchParams.push(`disasterMode=${searchCondition.disasterMode}`)
        }
        if (searchCondition.disasterPrevention) {
            searchParams.push(`disasterPrevention=${searchCondition.disasterPrevention}`)
        }
        const searchParamsText: string = searchParams.join('&')
        return `/api/admin/disasters/mode/statuses/export?${searchParamsText}`
    }

    // トピック登録（管理）
    async registerDisasterTopic(
        condominiumCd: string,
        request: NatPostAdminDisasterRegisterParams
    ): Promise<NatPostAdminDisasterRegister> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics`,
            {
                title: request.title,
                category: request.category,
                description: request.description
            },
            ContentType.JSON
        )
    }

    // 防災情報登録
    async createDisasterInfo(
        condominiumCd: string,
        request: NatPutDisasterPreventionRequest
    ): Promise<NatPutDisasterPreventionResponse> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/preventions`,
            {
                title: request.title,
                description: request.description,
                releaseSetting: request.releaseSetting,
                disasterPreventionFile: request.disasterPreventionFile
            },
            ContentType.JSON,
        )
    }

    // ファイルアップロード（防災情報）
    async createDisasterInfoFile(
        condominiumCd: string,
        disasterPreventionId: number,
        request: NatPostDisasterPreventionFileRequest
    ): Promise<NatPostDisasterPreventionFileResponse> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}/files`,
            {
                fileName: request.fileName,
                fileData: request.fileData,
                publishMaterialName: request.publishMaterialName,
                sortOrder: request.sortOrder,
                fileIndex: request.fileIndex
            },
            ContentType.JSON,
        )
    }

    // トピック登録（居住者）
    async registerDisasterTopicResident(
        condominiumCd: string,
        request: NatPostDisasterTopicRegisterRequest
    ): Promise<NatPostDisasterTopicRegisterResult> {
        return await this.http.post(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics`,
            {
                category: request.category,
                title: request.title,
                description: request.description
            },
            ContentType.JSON,
        )
    }

    // 災害時コメント検索（管理）
    async searchDisasterCommentList(
        condominiumCd: string,
        searchCondition?: SearchCondition,
        searchConditionSortInfo?: SortOrder[],
    ): Promise<NatGetDisasterCommentList> {
        const sortInfos: string[] = []
        if (searchConditionSortInfo) {
            searchConditionSortInfo.forEach((sortInfo) => {
                sortInfos.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }

        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/comments/search`,
            {
                category: searchCondition?.category || null,
                startPostDate: searchCondition?.startPostDate || null,
                endPostDate: searchCondition?.endPostDate || null,
                keyword: searchCondition?.keyword || null,
                commentType: searchCondition?.commentType || null,
                pageNum: searchCondition?.pageNum ?? null,
                page: searchCondition?.page ?? null,
                sortInfo: sortInfos
            },
            ContentType.JSON
        )
    }

    // 災害時コメントデータ出力
    static exportDisasterCommentData(
        condominiumCd: string,
        searchCondition: SearchCondition
    ): string {
        // 今後、リクエストに個人情報を含む場合はPOST化を検討する
        const searchParams: string[] = []
        if (searchCondition.category) {
            searchParams.push(`category=${searchCondition.category}`)
        }
        if (searchCondition.startPostDate) {
            searchParams.push(`startPostDate=${searchCondition.startPostDate}`)
        }
        if (searchCondition.endPostDate) {
            searchParams.push(`endPostDate=${searchCondition.endPostDate}`)
        }
        if (searchCondition.keyword) {
            searchParams.push(`keyword=${searchCondition.keyword}`)
        }
        if (searchCondition.commentType) {
            searchParams.push(`commentType=${searchCondition.commentType}`)
        }
        const searchParamsText: string = searchParams.join('&')
        return `/api/admin/condominiums/${condominiumCd}/disasters/topics/comments/export?${searchParamsText}`
    }

    // 災害時トピック検索（管理）
    async searchDisasterTopicList(
        condominiumCd: string,
        searchCondition?: SearchCondition,
        searchConditionSortInfo?: SortOrder[],
    ): Promise<NatGetDisasterTopicList> {
        const sortInfos: string[] = []
        if (searchConditionSortInfo) {
            searchConditionSortInfo.forEach((sortInfo) => {
                sortInfos.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }

        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/search`,
            {
                category: searchCondition?.category || null,
                excludePastTopics: searchCondition?.ExcludePastTopics || null,
                pageNum: searchCondition?.pageNum ?? null,
                page: searchCondition?.page ?? null,
                sortInfo: sortInfos
            },
            ContentType.JSON
        )
    }

    // トピック取得（管理）
    async getAdminDisasterTopic(
        condominiumCd: string,
        topicId: number
    ): Promise<NatGetDisasterTopicForAdmin> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}`
        )
    }

    // トピック編集（管理）
    async editDisasterTopic(
        condominiumCd: string,
        topicId: number,
        request: NatPutAdminDisasterEditParams
    ): Promise<NatPutAdminDisasterTopicEdit> {
        return await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}`,
            {
                title: request.title,
                category: request.category,
                description: request.description,
                version: request.version
            },
        )
    }

    // 防災情報取得（居住者）
    async getDisasterInfoForResident(
        condominiumCd: string,
        disasterPreventionId: number
    ): Promise<NatGetDisasterPreventionsInfoForResidentResponse> {
        return await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`
        )
    }

    // 防災情報更新
    async editDisasterPreventionInfo(
        condominiumCd: string,
        disasterPreventionId: number,
        request: NatPutDisasterPreventionEditParams
    ): Promise<NatPutDisasterPreventionEdit> {
        return await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`,
            {
                title: request.title,
                description: request.description,
                releaseSetting: request.releaseSetting,
                version: request.version,
                disasterPreventionFile: request.disasterPreventionFile,
                disasterPreventionDeleteFile: request.disasterPreventionDeleteFile
            }
        )
    }

    // トピック編集（居住者）
    async editDisasterTopicResident(
        condominiumCd: string,
        topicId: number,
        request: NatPutDisasterTopicEditRequest
    ): Promise<NatBaseResponseInterface> {
        return await this.http.put(
            `/api/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}`,
            request,
        )
    }

    // 防災情報一覧取得（居住者）
    async getDisasterInfoList(
        condominiumCd: string
    ): Promise<NatGetDisasterPreventionsInfoListResponse> {
        return await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/disasters/preventions`
        )
    }

    // モード設定（管理）
    async settingDisasterMode(
        condominiumCdList: string[]
    ): Promise<NatBaseResponseInterface> {
        return await this.http.put(
            '/api/admin/disasters/mode/setting',
            {condominiumCdList: condominiumCdList}
        )
    }

    // トピック削除（管理）
    async deleteDisasterTopicForAdmin(
        condominiumCd: string,
        topicId: number
    ): Promise<NatBaseResponseInterface> {
        return await this.http.delete(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}`
        )
    }

    // コメント取得（管理）
    async getDisasterCommentForAdmin(condominiumCd: string, topicId: number, commentId?: number):
        Promise<NatGetDisasterCommentForAdmin> {
        return await this.http.get(commentId !== undefined
            ? `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`
            : `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments`
        )
    }

    // コメント投稿（管理）
    async createDisasterCommentForAdmin(
        condominiumCd: string,
        topicId: number,
        request: NatPostDisasterCommentForAdminRequest
    ): Promise<NatPostDisasterCommentForAdminResponse> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments`,
            request,
            ContentType.JSON
        )
    }

    // コメント更新（管理）
    async updateDisasterCommentForAdmin(
        condominiumCd: string,
        topicId: number,
        commentId: number,
        request: NatPutDisasterCommentForAdminRequest
    ): Promise<NatPutDisasterCommentForAdminResponse> {
        return await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`,
            request
        )
    }

    // コメント削除（管理）
    async deleteDisasterCommentForAdmin(condominiumCd: string, topicId: number, commentId: number):
        Promise<NatBaseResponseInterface> {
        return await this.http.delete(
            `/api/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`
        )
    }

    // モード解除（管理）
    async releaseDisasterMode(
        condominiumCdList: string[]
    ): Promise<NatBaseResponseInterface> {
        return await this.http.put(
            '/api/admin/disasters/mode/release',
            {condominiumCdList: condominiumCdList}
        )
    }
}